import {
  Box,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { InventoryAdjustmentLotStatusIcon } from "./InventoryAdjustmentLotStatusIcon"
import { inventoryAdjustmentQtyInputSchema } from "./schemas"

import { AerosNumericField } from "@/components/UiKit/AerosNumericField/AerosNumericField"
import {
  type InventoryPalletGroup,
  type InventoryPalletLot,
  type ProductPackagingType,
} from "@/graphql/codegen/graphql"
import { calculateRemainingShelfLife } from "@/screens/Tablet/utils/utils"
import { formatNumber } from "@/utils/number"
import { getProductPackagingTypeLabel } from "@/utils/product"
import { isNil } from "@/utils/utils"

export interface SelectedLot {
  lot: InventoryPalletLot
  pallet: InventoryPalletGroup
}

interface InventoryAdjustmentQuantityFormProps {
  selectedLots: SelectedLot[]
  newUomQtyByLotId: Record<string, number | undefined>
  uomName: string
  onQuantityChange: (lotId: string, qty: number | undefined) => void
  onLotRemove: (lotId: string) => void
}

export const InventoryAdjustmentQuantityForm: FC<InventoryAdjustmentQuantityFormProps> = ({
  selectedLots,
  newUomQtyByLotId,
  uomName,
  onQuantityChange,
  onLotRemove,
}) => {
  const handleQuantityChange = (lotId: string, value: number | null) => {
    const isValid = inventoryAdjustmentQtyInputSchema.safeParse(value).success
    onQuantityChange(lotId, isValid ? (value as number) : undefined)
  }

  return (
    <Box className='rounded-lg border border-gray-300 p-4'>
      <Typography variant='subtitle1' className='mb-4 font-bold'>
        2. Specify Quantities
      </Typography>
      <TableContainer sx={{ maxHeight: 360, overflow: "auto" }}>
        <Table size='small' stickyHeader sx={{ tableLayout: "auto", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Reference #</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Location</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Lot #</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Batch #</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Remaining Shelf Life</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Packaging</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Units</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Pkg UoM</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Current Lot UoM</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                New UoM Qty<span className='ml-0.5 text-red-500'>*</span>
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Adjustment Qty</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {!selectedLots.length && (
              <TableRow>
                <TableCell colSpan={12} className='py-10'>
                  <p className='flex flex-col items-center gap-y-2 text-center'>
                    <span className='font-semibold text-gray-700'>No lots selected</span>
                    <span className='max-w-[275px] text-gray-500'>
                      Select lot(s) from the table above using the checkboxes to adjust quantities.
                    </span>
                  </p>
                </TableCell>
              </TableRow>
            )}

            {selectedLots.map(({ lot, pallet }) => {
              const remainingShelfLife =
                lot.expirationDate && lot.manufacturingDate
                  ? calculateRemainingShelfLife(lot.manufacturingDate, lot.expirationDate)
                  : null

              const newUomQty = newUomQtyByLotId[lot.rawInventoryId] ?? null
              const validationResult = isNil(newUomQty) ? null : inventoryAdjustmentQtyInputSchema.safeParse(newUomQty)
              const inputError = validationResult?.success === true ? null : validationResult?.error.errors[0].message
              const adjustmentQty = isNil(newUomQty) || isNil(lot.totalUnits) ? null : newUomQty - lot.totalUnits
              const isLotDeletion = newUomQty === 0

              return (
                <TableRow
                  key={lot.rawInventoryId}
                  data-testid={`inventory-adjustment.specify-quantities.lot-row-${lot.rawInventoryId}`}
                >
                  <TableCell>{pallet.referenceNbr}</TableCell>
                  <TableCell>{pallet.location}</TableCell>
                  <TableCell>
                    <Box className='flex items-center gap-x-1'>
                      <InventoryAdjustmentLotStatusIcon qaStatus={lot.qaStatus} />
                      {lot.lotNbr}
                    </Box>
                  </TableCell>
                  <TableCell>{lot.batchNbr ?? ""}</TableCell>
                  <TableCell>{remainingShelfLife === null ? "" : `${remainingShelfLife}%`}</TableCell>
                  <TableCell>{getProductPackagingTypeLabel(lot.packageDesc as ProductPackagingType)}</TableCell>
                  <TableCell>{lot.totalPackages}</TableCell>
                  <TableCell>
                    {lot.packageSize} {uomName}
                  </TableCell>
                  <TableCell>
                    {lot.totalUnits} {uomName}
                  </TableCell>
                  <TableCell>
                    <Box className='flex flex-col gap-y-0.5'>
                      <AerosNumericField
                        size='small'
                        value={newUomQty ?? null}
                        error={!!inputError}
                        allowNegative={false}
                        decimalScale={2}
                        slotProps={{
                          input: { endAdornment: <InputAdornment position='end'>{uomName}</InputAdornment> },
                        }}
                        onChange={(value) => handleQuantityChange(lot.rawInventoryId, value)}
                        sx={{ width: 140 }}
                        data-testid={`inventory-adjustment.specify-quantities.new-qty-field-${lot.rawInventoryId}`}
                      />
                      {inputError && (
                        <Typography variant='caption' className='text-secondary'>
                          {inputError}
                        </Typography>
                      )}
                      {isLotDeletion && !inputError && (
                        <Typography
                          variant='caption'
                          className='text-secondary'
                          data-testid={`inventory-adjustment.specify-quantities.lot-deletion-warning-${lot.rawInventoryId}`}
                        >
                          Lot will be deleted
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {adjustmentQty !== null && (
                      <Typography variant='body2' className={adjustmentQty < 0 ? "text-red-600" : "text-green-600"}>
                        {adjustmentQty > 0 ? "+" : ""}
                        {formatNumber(adjustmentQty)} {uomName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size='small'
                      onClick={() => onLotRemove(lot.rawInventoryId)}
                      data-testid={`inventory-adjustment.specify-quantities.remove-lot-button-${lot.rawInventoryId}`}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
